import React, { useContext, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CheckCircle from '@material-ui/icons/CheckCircle';
import Delete from '@material-ui/icons/DeleteOutline';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Chip from '@material-ui/core/Chip';
import { useTranslation } from '../../hooks/useTranslation';
import colors from '../../themes/main-colors';
import { NotificationsContext } from '../../providers/NotificationsProvider';
import useWrapWithLoader from '../../hooks/useWrapWithLoading';
import { useTrustedDevice } from '../../hooks/useTrustedDevice';
import { parseDeviceName } from '../../util/parseDeviceName';
import Modal from '../modal';
import PGEButton from '../buttons';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      marginTop: theme.spacing(2),
      flexWrap: 'nowrap',
    },
    cardContent: {
      paddingBottom: '16px !important',
    },
    title: {
      marginTop: theme.spacing(4),
    },
    deleteMFA: {
      display: 'flex',
      justifyContent: 'flex-end',
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'flex-start',
        marginTop: theme.spacing(2),
      },
    },
    pointer: {
      cursor: 'pointer',
    },
    body: {
      maxWidth: 600,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
      '& > *': {
        marginTop: theme.spacing(1),
      },
    },
    cancelButton: {
      border: 'none !important',
      marginRight: theme.spacing(1),
    },
  }),
);

const TrustedDeviceList = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const notificationContext = useContext(NotificationsContext);
  const { wrapWithLoader } = useWrapWithLoader();
  const {
    devices,
    loading,
    currentDeviceId,
    forgetDevice,
  } = useTrustedDevice();
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [deletingDeviceId, setDeletingDeviceId] = useState<string>('');

  const showNotification = (
    message: string,
    type: 'error' | 'success' = 'success',
  ) => {
    notificationContext.setState({
      isOpen: true,
      message,
      severity: type,
    });
    window.scrollTo(0, 0);
  };

  const removeTrustedDevice = wrapWithLoader(async () => {
    try {
      setDeleteConfirmation(false);
      if (!deletingDeviceId) {
        return;
      }

      await forgetDevice(deletingDeviceId);
      setDeletingDeviceId('');
      showNotification(t('TWO_STEP_AUTH_TRUSTED_DEVICE_REMOVED'));
    } catch (error) {
      showNotification(t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'), 'error');
    }
  });

  if ((loading && devices.length === 0) || devices.length === 0) {
    return null;
  }

  return (
    <>
      <Grid item xs={12} className={classes.title}>
        <Typography variant={'h2'}>{t('YOUR_TRUSTED_DEVICES')}</Typography>
      </Grid>

      {devices.map(device => (
        <Card key={device.id} className={classes.container}>
          <CardContent className={classes.cardContent}>
            <Grid
              container
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingLeft: 16,
                paddingRight: 16,
                paddingTop: 8,
                paddingBottom: 8,
              }}
            >
              <Grid item style={{ display: 'flex' }}>
                <Typography
                  style={{ display: 'flex', alignItems: 'center' }}
                  component={'div'}
                >
                  <CheckCircle
                    htmlColor={colors.shoreGreen}
                    fontSize="small"
                    style={{ marginRight: '8px' }}
                  />
                  {parseDeviceName(device?.name) || t('UNKNOWN_DEVICE')}
                  {device.id === currentDeviceId && (
                    <Chip
                      variant="outlined"
                      color="primary"
                      size="small"
                      label="Current"
                      style={{
                        fontFamily: 'Untitled-Sans',
                        marginLeft: '8px',
                      }}
                    />
                  )}
                </Typography>
              </Grid>
              <Grid item className={classes.deleteMFA}>
                <IconButton>
                  <Delete
                    htmlColor={colors.midGrey}
                    onClick={() => {
                      setDeleteConfirmation(true);
                      setDeletingDeviceId(device.id);
                    }}
                    className={classes.pointer}
                    aria-label="delete-icon-btn"
                    role="button"
                  />
                </IconButton>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      <Modal
        open={deleteConfirmation}
        showCloseIcon={false}
        title={
          <Typography variant="h2">
            {t('DELETE_TRUSTED_DEVICE_MODAL_TITLE')}
          </Typography>
        }
      >
        <Box className={classes.body}>
          <Typography>{t('DELETE_TRUSTED_DEVICE_MODAL_DESC')}</Typography>
        </Box>
        <Box justifyContent="flex-end" display="flex">
          <PGEButton
            className={classes.cancelButton}
            color={'secondary'}
            variant={'outlined'}
            onClick={() => {
              setDeleteConfirmation(false);
              setDeletingDeviceId('');
            }}
          >
            {t('CANCEL')}
          </PGEButton>
          <PGEButton
            color={'primary'}
            variant={'contained'}
            onClick={() => removeTrustedDevice()}
            aria-label="delete-btn"
          >
            {t('DELETE')}
          </PGEButton>
        </Box>
      </Modal>
    </>
  );
};

export default TrustedDeviceList;
